import { Component, CUSTOM_ELEMENTS_SCHEMA, ChangeDetectorRef, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppService } from '../../core/service/app.service';
import { Router, NavigationEnd, Event as RouterEvent, RouterOutlet, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { FooterComponent } from '../footer/footer';
import { HeaderComponent } from '../header/header';
import { SidebarComponent } from '../sidebar/sidebar';
import { ThemeCustomizerComponent } from '../theme-customizer/theme-customizer';
import { NgClass } from '@angular/common';
import { SessionWarningDialogComponent } from '../../shared/session-warning-dialog/session-warning-dialog.component';
import { ProfileCompletionAlertPopupComponent } from '../../shared/profile-completion-alert-popup/profile-completion-alert-popup.component';
import { TimezoneCompletionAlertPopupComponent } from '../../shared/timezone-completion-alert-popup/timezone-completion-alert-popup.component';
import { SessionService } from '../../core/service/session.service';
import { AuthService } from '../../auth/auth.service';
import { CurrencyService } from '../../core/service/currency.service';
import { VendorDateTimeService } from '../../core/service/vendor-datetime.service';
import { TimezoneOnboardingStore } from '../../shared/store/timezone-onboarding.store';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-root',
    templateUrl: './app-layout.html',
    standalone: true,
    imports: [NgClass, ThemeCustomizerComponent, SidebarComponent, HeaderComponent, RouterOutlet, FooterComponent, SessionWarningDialogComponent, TimezoneCompletionAlertPopupComponent, ProfileCompletionAlertPopupComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppLayout {
    private readonly vendorDateTimeService = inject(VendorDateTimeService);
    private readonly timezoneOnboardingStore = inject(TimezoneOnboardingStore);
    store: any;
    showTopButton = false;
    currentModule: string = '';
    showWarning = false;
    private readonly sessionWarningSubscription?: Subscription;
    private currencyChangeSubscription?: Subscription;

    constructor(
        public translate: TranslateService,
        public storeData: Store<any>,
        private readonly service: AppService,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly sessionService: SessionService,
        private readonly authService: AuthService,
        private readonly cdRef: ChangeDetectorRef,
        private readonly currencyService: CurrencyService
    ) {
        this.currentModule = this.activatedRoute.snapshot?.url[0]?.path || '';
        // Session timer starts automatically when service is initialized
        this.sessionWarningSubscription = this.sessionService.showWarning.subscribe((show) => {
            this.showWarning = show;
            this.cdRef.detectChanges();
        });
    }
    headerClass = '';
    contentPaddingClass: string = 'px-6 py-4';
    ngOnInit() {
        this.initStore();
        this.initAnimation();
        this.toggleLoader();
        if (this.vendorDateTimeService.isTimeZoneMissing()) {
            this.timezoneOnboardingStore.open();
        }
        // Manage content padding based on current route
        const computePadding = (url: string): string => {
            // Add your rules here
            if (url.startsWith('/users/profile')) return 'p-0';
            if (url.startsWith('/system-preference-master/add-new-form')) return 'p-0';
            if (url.startsWith('/master/user-detail')) return 'p-0';
            if (url.startsWith('/vendor-master/vendor-detail')) return 'p-0';
            if (url.startsWith('/chat')) return 'p-0';
            if (url.startsWith('/order-master/order-list')) return 'px-6 py-4';
            if (url.startsWith('/contract-master/contract-view')) return 'p-0';
            if (url.startsWith('/vendor/profile')) return 'p-0';
            if (url.startsWith('/vendor/vendor-rating-details')) return 'p-0';
            return 'px-6 py-4';
        };

        // initialize once on load
        this.contentPaddingClass = computePadding(this.router.url || '');

        // update on navigation
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                const url = event.urlAfterRedirects || event.url;
                this.contentPaddingClass = computePadding(url);
            }
        });
        window.addEventListener('scroll', () => {
            if (document.body.scrollTop > 50 || document.documentElement.scrollTop > 50) {
                this.showTopButton = true;
            } else {
                this.showTopButton = false;
            }
        });

        this.currencyChangeSubscription = this.currencyService.currencyChange$.subscribe(() => {
            this.cdRef.detectChanges();
        });
    }

    ngOnDestroy() {
        window.removeEventListener('scroll', () => {});
        if (this.sessionWarningSubscription) {
            this.sessionWarningSubscription.unsubscribe();
        }
        if (this.currencyChangeSubscription) {
            this.currencyChangeSubscription.unsubscribe();
        }
    }

    initAnimation() {
        this.service.changeAnimation();
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.service.changeAnimation();
            }
        });

        const ele: any = document.querySelector('.animation');
        ele.addEventListener('animationend', () => {
            this.service.changeAnimation('remove');
        });
    }

    toggleLoader() {
        this.storeData.dispatch({ type: 'toggleMainLoader', payload: true });
        setTimeout(() => {
            this.storeData.dispatch({ type: 'toggleMainLoader', payload: false });
        }, 500);
    }

    initStore() {
        this.storeData
            .select((d) => d.index)
            .subscribe((d) => {
                this.store = d;
            });
    }

    goToTop() {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    }

    continueSession() {
        const refreshToken: any = this.authService.getRefreshToken();
        const payload = {
            refreshToken: refreshToken,
        };
        this.authService.authRefreshToken(payload).subscribe({
            next: (res: any) => {
                // Handle both response formats
                if (res?.data?.access_token) {
                    // New format
                    this.authService.setToken(res.data.access_token);
                    if (res.data.refresh_token) {
                        this.authService.setRefreshToken(res.data.refresh_token);
                    }
                    if (res.data.access_token_expiry) {
                        this.authService.setAccessTokenExpiry(res.data.access_token_expiry);
                    }
                } else if (res?.data?.token) {
                    // Alternative format
                    this.authService.setToken(res.data.token);
                    if (res.data.refreshToken) {
                        this.authService.setRefreshToken(res.data.refreshToken);
                    }
                    if (res.data.access_token_expiry) {
                        this.authService.setAccessTokenExpiry(res.data.access_token_expiry);
                    }
                } else if (res?.token) {
                    // Old format
                    this.authService.setToken(res.token);
                    if (res.refreshToken) {
                        this.authService.setRefreshToken(res.refreshToken);
                    }
                    if (res.expiry) {
                        this.authService.setAccessTokenExpiry(res.expiry);
                    }
                }

                // Confirm stay active after successful token refresh
                this.sessionService.confirmStayActive();
            },
            error: (err: any) => {
                this.logout();
            },
        });
    }

    

    logout() {
        this.sessionService.logoutUser();
    }
}
